import React, {useEffect} from 'react';
import {connect} from 'react-redux';
import useSWR from 'swr';
import PropTypes from 'prop-types';
import {Page, Tile, DescriptiveText, Columnizer, Row, Icon} from '../../../shared';
import {_openModal} from '../../../shared/reducers/modals';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';
import AccountCard from '../../components/accounts/AccountCard';
import axiosInstance from '../../../shared/utils/axios';
import StickyHeaderBar from '../../shared/components/StickyHeaderBar';
import qs from 'qs';
import history from '../../../history';
import {calculateAggregationStatus} from '../../../shared/utils/utils';
import {_setAggregationStatus} from '../../../shared/reducers/user';

const formatErrorMessage = (institution, brand) => {
  if (institution.errorMessage) {
    return institution.errorMessage
      .replace(/{{Institution}}/g, institution.name)
      .replace(/(<([^>]+)>)/ig, '')
      .replace(/{{branded support}}/g, brand.investorCSEmail);
  }

  switch (institution.status) {
    case 'YODLEE_ERROR':
      return `There is an issue communicating between ${institution.name} and our site. \
       Client services is looking into this issue and will contact you as soon as possible.`;
    case 'AUTH_FAILED':
      return `${institution.name} has requested that you verify your login information, \
       which you can do by clicking here. Until this is completed you won't see updated data from ${institution.name}.`;
    default:
      return 'An error has occurred';
  }
};

const getErrorMessageCallback = (institution) => {
  if (institution.status === 'AUTH_FAILED') {
    const refreshFlowParams = {providerAccountId: institution.id, flow: 'refresh'};
    return () => history.push(`/accounts/aggregation?${qs.stringify(refreshFlowParams)}`);
  }

  return null;
};

const Accounts = ({openModal, session, setAggregationStatus}) => {
  const {data: institutionsData, mutate: refreshAccountLists, isValidating: institutionsLoading} = useSWR('/user/institutions');

  const removeInstitution = (institution) => openModal({
    type: 'ConfirmModal',
    title: 'Remove Institution',
    body: <DescriptiveText preset={'standard'}>
      You are about to remove {institution.name} and all accounts linked to it from CircleBlack.
      If you are sure this is what you want, then click Remove.
    </DescriptiveText>,
    button: {
      text: 'Remove',
      action: () => axiosInstance.delete(`/yodlee/institutions/${institution.id}`)
        .then(() => refreshAccountLists())
        .catch(err => openModal({type: 'Error', title: 'Remove Institution', err: err})),
      size: 'sm',
      width: '110px',
    },
  });

  const removeAccount = (accountId) => openModal({
    type: 'ConfirmModal',
    title: 'Remove Account',
    body: <DescriptiveText preset={'standard'}>This will permanently remove the account and any history associated with it. You can
      add this account back later if you wish.</DescriptiveText>,
    button: {
      text: 'Remove',
      action: () => axiosInstance.delete(`/accounts/${accountId}/manual`)
        .then(() => refreshAccountLists())
        .catch(err => openModal({type: 'Error', title: 'Remove Account', err: err})),
      size: 'sm',
      width: '110px',
    },
  });

  const editAccount = (account) => openModal({
    type: 'EditAccountModal',
    account: account,
    onSuccess: () => refreshAccountLists(),
  });

  const institutions = institutionsData && institutionsData.map((institution, index) => (
    <Tile key={institution.id}
          title={institution.name}
          height={'fit-content'}
          margin={index === institutionsData.length - 1 ? '10px 10px 20px 10px' : '10px'}
          actionIcon={institution.sourceType === 'AGGREGATOR' && (
            <Icon width={'30px'}
                  height={'30px'}
                  svg={'close'}
                  action={() => removeInstitution(institution)}/>
          )}>
      {['YODLEE_ERROR', 'AUTH_FAILED'].includes(institution.status) && (
        <Row wrap={false}
             action={getErrorMessageCallback(institution)}
             borderRadius={'6px'}
             margin={'5px'}
             justifySubRow={'left'}
             backgroundColor={'rgba(255,255,255,0.1)'}>
          <Icon svg={'attention'}
                color={'#FFC003'}
                height={'20px'}
                width={'20px'}
                margin={'5px 15px 5px 15px'}
          />
          <DescriptiveText margin={'5px'} alignment={'left'} layout={{grow: 1}}>
            {formatErrorMessage(institution, session.profile.brand)}
          </DescriptiveText>
        </Row>
      )}
      <Columnizer alignItems="top" height="auto" justifySubRow="left" itemWidth={260} wrap={true}>
        {institution.accounts.map(account => (
          <AccountCard key={account.id}
                       institution={institution}
                       account={account}
                       removeAccount={removeAccount}
                       editAccount={editAccount}/>
        ))}
      </Columnizer>
    </Tile>
  ));

  useEffect(() => {
    if (institutionsData) {
      setAggregationStatus(calculateAggregationStatus(institutionsData));
    }
  }, [institutionsData]);

  if (institutionsLoading) {
    return <LoadingSpinner/>;
  }

  return <Page>
    <StickyHeaderBar title={'ACCOUNTS'}
                     buttons={{
                       primary: {
                         label: 'Add Accounts',
                         action: () => openModal({
                           type: 'AddAccount',
                           onSuccess: () => refreshAccountLists(),
                         }),
                       },
                     }}/>
    {institutions}
  </Page>;
};

const mapStateToProps = state => ({
  session: state.session,
});

const mapDispatchToProps = dispatch => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
  setAggregationStatus: (status) => dispatch(_setAggregationStatus(status)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Accounts);

Accounts.propTypes = {
  /**
   * Session
   */
  session: PropTypes.shape({
    runAsInvestor: PropTypes.number.isRequired,
    profile: PropTypes.shape({
      brand: PropTypes.object.isRequired,
    }).isRequired,
  }).isRequired,
  /**
   * The open function that dispatches _openModal from Redux
   */
  openModal: PropTypes.func.isRequired,
  /**
   * The function that dispatches _setAggregationStatus from Redux
   */
  setAggregationStatus: PropTypes.func.isRequired,
};
